import type { KeyboardEvent } from "react";

import { AnimatePresence, motion } from "framer-motion";

import { DocumentItem, Icon, Span } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn, useIsOpen } from "@virtual-brain-lib/frontend-design-system/utils";
import {
	CODE_BASED_TABLE_ANALYSIS_NAME_SEPARATOR,
	type MessageToolCodeBasedTableAnalysisDocument,
	type MessageToolStepStatus,
} from "@virtual-brain-lib/types";

import { CodeBasedTableAnalysisStepRow } from "./CodeBasedTableAnalysisStepRow";

export function CodeBasedTableAnalysisDocumentRow({
	document,
	onDocumentClick,
}: {
	readonly document: MessageToolCodeBasedTableAnalysisDocument;
	readonly onDocumentClick?: (doc: MessageToolCodeBasedTableAnalysisDocument) => void;
}) {
	const showStatusText = document.status === "processing" && Boolean(document.statusText);
	const hasSteps = document.steps.length > 0;
	const { isOpen, toggleIsOpen } = useIsOpen({ defaultIsOpen: false, isEnabled: hasSteps });

	const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			toggleIsOpen();
		}
	};

	return (
		<div className="flex flex-col gap-1.5">
			<div
				className={cn(
					"flex min-w-0 flex-wrap items-center gap-x-1.5 rounded-sm",
					hasSteps ? "cursor-pointer hover:bg-primary-light" : "cursor-default",
				)}
				role={hasSteps ? "button" : undefined}
				tabIndex={hasSteps ? 0 : undefined}
				onClick={hasSteps ? toggleIsOpen : undefined}
				onKeyDown={hasSteps ? handleHeaderKeyDown : undefined}
			>
				<div className="flex max-w-full min-w-0 items-center gap-1.5">
					<DocumentRowStatusIcon status={document.status} />
					<span className="min-w-0 flex-1">
						<DocumentItem
							className="min-w-0"
							mimeType={document.mimeType}
							name={document.name}
							tooltip={{
								children: document.name
									.split(CODE_BASED_TABLE_ANALYSIS_NAME_SEPARATOR)
									.join("\n"),
								className: "max-w-120 whitespace-pre-line",
							}}
							onClick={
								onDocumentClick
									? () => {
											onDocumentClick(document);
										}
									: undefined
							}
						/>
					</span>
					{hasSteps && (
						<Icon
							className={cn(
								"shrink-0 transition-transform duration-200 ease-out",
								isOpen && "rotate-90",
							)}
							color="defaultLight"
							name="chevronRight"
							size="sm"
						/>
					)}
				</div>
				{showStatusText && !isOpen && (
					<Span
						className="block max-w-full min-w-0 truncate italic"
						color="defaultLight"
						variant="body-xs"
					>
						{document.statusText}
					</Span>
				)}
			</div>
			<AnimatePresence initial={false}>
				{hasSteps && isOpen && (
					<motion.div
						key="steps"
						className="overflow-hidden"
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: "auto", opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
					>
						<div className="ml-4.5 flex flex-col gap-1">
							<AnimatePresence initial={false}>
								{document.steps.map((step) => (
									<motion.div
										key={step.id}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0 }}
										initial={{ opacity: 0, y: 2 }}
										transition={{ duration: 0.2, ease: "easeOut" }}
									>
										<CodeBasedTableAnalysisStepRow step={step} />
									</motion.div>
								))}
							</AnimatePresence>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}

function DocumentRowStatusIcon({ status }: { readonly status: MessageToolStepStatus }) {
	if (status === "queued") {
		return (
			<span className="flex size-3 shrink-0 items-center justify-center">
				<span className="size-1 rounded-full bg-default/65" />
			</span>
		);
	}

	return (
		<Icon
			className="shrink-0"
			color="defaultLight"
			name={status === "processing" ? "spinner" : "circleCheck"}
			size="sm"
		/>
	);
}
